import React, { useState, useEffect } from 'react';
import { BookOpen, Code, CheckCircle2, ChevronRight, Cpu } from 'lucide-react';

const App = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const retroWords = [
    "INSERT COIN", "HIGH SCORE: 9999", "LEVEL UP", "POLYMORPHISM UNLOCKED", 
    "ENCAPSULATION ACTIVATED", "SYSTEM BOOT...", "JAVA VIRTUAL MACHINE", 
    "OBJECT ORIENTED", "PRESS START", "GAME OVER", "CONTINUE? 9...8...7..."
  ];

  const quizCards = [
    {
      title: "Theoretical Mastery",
      description: "Deep dive into the conceptual pillars of Java OOP. Test your understanding of classes and objects.",
      icon: <BookOpen className="w-10 h-10" />,
      link: "https://lambent-queijadas-8cb45a.netlify.app/",
      accent: "from-cyan-400 to-blue-600",
      tag: "Concepts"
    },
    {
      title: "Code Challenger",
      description: "Practical code-based scenarios. Debug, analyze, and predict the output of complex snippets.",
      icon: <Code className="w-10 h-10" />,
      link: "https://majestic-frangollo-17e976.netlify.app/",
      accent: "from-fuchsia-500 to-purple-600",
      tag: "Implementation"
    },
    {
      title: "Rapid Fire T/F",
      description: "Fast-paced True or False questions to sharpen your instincts on Java syntax and rules.",
      icon: <CheckCircle2 className="w-10 h-10" />,
      link: "https://quizappoop.netlify.app/",
      accent: "from-yellow-400 to-orange-500",
      tag: "Speed"
    }
  ];

  const comingSoonItems = [
    "Packages & Interfaces",
    "UML Modeling",
    "Error Handling",
    "Exception Hierarchy"
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-fuchsia-500 selection:text-white font-['Space_Grotesk'] overflow-x-hidden relative">
      {/* Font Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Outfit:wght@300;400;600&display=swap');
        
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        .font-body {
          font-family: 'Outfit', sans-serif;
        }
      `}</style>

      {/* Scanline CRT Effect Overlay */}
      <div className="fixed inset-0 pointer-events-none z-[100] opacity-[0.03] bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] bg-[length:100%_2px,3px_100%]"></div>

      {/* Retro Scrolling Banner */}
      <div className="bg-fuchsia-600 py-3 overflow-hidden whitespace-nowrap border-b-4 border-cyan-400 sticky top-0 z-50 shadow-[0_0_20px_rgba(255,0,255,0.5)]">
        <div className="flex animate-marquee">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex shrink-0">
              {retroWords.map((word, idx) => (
                <span key={idx} className="mx-8 text-white font-bold text-xl italic tracking-tight uppercase drop-shadow-[2px_2px_0px_#22d3ee]">
                  {word} •
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <header className="relative py-24 px-6 text-center overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-t from-transparent via-fuchsia-600/20 to-transparent blur-3xl -z-10"></div>
        
        <h1 className="text-6xl md:text-9xl font-bold mb-6 tracking-tighter uppercase italic leading-[0.9]">
          <span className="text-cyan-400 drop-shadow-[0_0_10px_#22d3ee]">JAVA</span> <span className="text-fuchsia-500 drop-shadow-[0_0_15px_#d946ef]">OOP</span>
          <br />
          <span className="text-white drop-shadow-[4px_4px_0px_#d946ef]">QUEST</span>
        </h1>
        <p className="text-cyan-200/70 text-lg max-w-2xl mx-auto font-semibold uppercase tracking-[0.3em] bg-black/40 inline-block px-6 py-2 border border-cyan-400/30">
          SYSTEM STATUS: READY TO COMPILE...
        </p>
      </header>

      {/* Quiz Cards Grid */}
      <main className="max-w-7xl mx-auto px-6 pb-32 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {quizCards.map((card, index) => (
            <a 
              key={index}
              href={card.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-fuchsia-600 to-cyan-400 rounded-lg blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
              
              <div className="relative h-full bg-black border-2 border-cyan-400/50 p-8 rounded-lg transform transition-all duration-300 group-hover:-translate-y-4 group-hover:border-fuchsia-500 overflow-hidden shadow-2xl">
                
                <div className="flex justify-between items-start mb-6">
                  <div className="p-3 bg-cyan-900/30 border border-cyan-400/50 rounded-lg text-cyan-400 group-hover:text-fuchsia-400 group-hover:border-fuchsia-500 transition-colors">
                    {card.icon}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-cyan-400 text-black px-2 py-1 rounded">
                    {card.tag}
                  </span>
                </div>

                <h3 className="text-2xl font-bold italic mb-4 text-white group-hover:text-cyan-400 transition-colors uppercase tracking-tight">
                  {card.title}
                </h3>
                
                <p className="font-body text-gray-400 text-base leading-relaxed mb-8">
                  {card.description}
                </p>

                <div className="flex items-center text-[11px] font-bold uppercase tracking-[0.2em] text-fuchsia-500 group-hover:text-cyan-400 transition-colors">
                  [ EXECUTE_QUIZ ] <ChevronRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
                
                <div className="absolute bottom-0 right-0 w-16 h-16 opacity-10 pointer-events-none" style={{backgroundImage: 'radial-gradient(#22d3ee 1px, transparent 1px)', backgroundSize: '8px 8px'}}></div>
              </div>
            </a>
          ))}
        </div>
      </main>

      {/* "Coming Soon" Neon Grid Section */}
      <section className="relative bg-black py-32 border-t-8 border-fuchsia-600 overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: `
            linear-gradient(rgba(217, 70, 239, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(217, 70, 239, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          transform: 'perspective(500px) rotateX(60deg) translateY(-100px)',
          transformOrigin: 'top'
        }}></div>

        <div className="relative z-10 flex flex-col items-center justify-center text-center px-4">
          <div className="bg-yellow-400 text-black px-12 py-6 border-4 border-black shadow-[8px_8px_0px_#d946ef] mb-12">
            <h2 className="text-4xl md:text-6xl font-bold italic uppercase tracking-tighter animate-pulse">
              COMING SOON
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl w-full">
            {comingSoonItems.map((item, i) => (
              <div key={i} className="border-4 border-cyan-400 bg-black/80 p-6 flex items-center justify-center">
                <p className="font-bold text-cyan-400 text-lg uppercase tracking-widest opacity-40">Locked: {item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-12 text-center text-cyan-400/60 border-t border-cyan-400/10 font-bold text-[10px] tracking-[0.3em] bg-black">
        <div className="flex items-center justify-center gap-2 mb-2">
          <span>System: Farah Magdy</span>
          <Cpu size={10} className="text-cyan-400" />
        </div>
        <p>Trace & Execution Module v2.0</p>
      </footer>
    </div>
  );
};

export default App;
